import React, {FocusEvent} from 'react';
import {compose} from 'recompose';
import {Field, useForm, useFormState} from 'react-final-form';
import block from 'bem-cn-lite';
import {FormFieldText, FormFieldSelect, ValidationUtils} from '@yandex-data-ui/cloud-components';
import {HTML} from '@doc-tools/components';

import {I18nKFunction} from 'i18n';
import withData from 'components/calculator/withData';
import {WithLocaleProps} from 'hoc/withLocale';
import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';
import CalculatorForm from 'components/calculator/CalculatorForm/CalculatorForm';
import CalculatorFormLayout from 'components/calculator/CalculatorFormLayout/CalculatorFormLayout';
import FieldBottomLabel from 'components/calculator/FieldBottomLabel/FieldBottomLabel';
import {getConfig} from 'units/calculator/api';

import './SupportPricingForm.scss';

export interface SupportPricingFormFields {
    template: string;
    consumption: string;
}

export interface SupportPricingFormOwnProps {
    title: string;
    calculatedConsumption?: string;
}

interface SupportPricingFormData {
    templates: string[];
}
export interface SupportPricingFormDataProps {
    data: SupportPricingFormData;
}

type PageInnerProps = SupportPricingFormOwnProps;
type SupportPricingFormProps = SupportPricingFormDataProps & PageInnerProps & WithTranslationProps;

const b = block('SupportPricingForm');
const CONSUMPTION_DEFAULT = '0';

function getTemplateItems(templates: string[], i18nK: I18nKFunction) {
    return templates.map((template) => ({title: i18nK(template), value: template}));
}

const SupportPricingForm: React.FC<SupportPricingFormProps> = (props) => {
    const {
        data: {templates},
        title,
        i18nK,
    } = props;

    const {change, initialize} = useForm<SupportPricingFormFields>();
    const {
        values: {template, consumption},
        initialValues,
    } = useFormState<SupportPricingFormFields>();

    React.useEffect(() => {
        initialize({
            consumption: CONSUMPTION_DEFAULT,
            template: templates[0],
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!initialValues) {
        return null;
    }

    const isPremium = template.includes('premium');
    const templateItems = getTemplateItems(templates, i18nK);

    return (
        <CalculatorForm title={title} className={b()}>
            <CalculatorFormLayout>
                <CalculatorFormLayout.Section>
                    <CalculatorFormLayout.Row
                        title={i18nK('field_support-consumption')}
                        note={i18nK('field_support-consumption-note')}
                        className={b('consumption')}
                    >
                        <Field name="consumption">
                            {(fieldProps) => (
                                <FormFieldText
                                    {...fieldProps}
                                    size="l"
                                    accept={(value: string) =>
                                        value.length <= 10 &&
                                        //fix negative numbers problem
                                        /^[0-9.]+$/.test(value) &&
                                        ValidationUtils.isFloat(value)
                                    }
                                    disabled={isPremium}
                                    onBlur={(e: FocusEvent) => {
                                        if (e && consumption === '') {
                                            change('consumption', CONSUMPTION_DEFAULT);
                                            e.preventDefault();
                                        }
                                    }}
                                />
                            )}
                        </Field>
                    </CalculatorFormLayout.Row>
                    <CalculatorFormLayout.Row
                        className={b('form-row')}
                        title={i18nK('field_support-template')}
                        note={i18nK('field_support-template-note')}
                    >
                        <Field name="template">
                            {(fieldProps) => (
                                <FormFieldSelect {...fieldProps} size="n" items={templateItems} />
                            )}
                        </Field>
                        {isPremium && (
                            <FieldBottomLabel>
                                <HTML>{i18nK('field_support-premium-link')}</HTML>
                            </FieldBottomLabel>
                        )}
                    </CalculatorFormLayout.Row>
                </CalculatorFormLayout.Section>
            </CalculatorFormLayout>
        </CalculatorForm>
    );
};

const formDataLoader = async (props: WithLocaleProps) => {
    const {locale} = props;
    return getConfig(locale, 'support');
};

export default compose<SupportPricingFormProps, SupportPricingFormOwnProps>(
    withData({loader: formDataLoader}),
    withTranslation('calculator'),
)(SupportPricingForm);
